import React, { useEffect, useState } from "react";

import { Card, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { isSubscriptionDisabled } from "../../../utils/appUtils";
import { renderFullName } from "../../../utils/utils";

import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { Empty } from "../../../components/common/Empty";
import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { UserStatusIcon } from "../users/UserStatusIcon";

const useStyles = makeStyles((theme) => ({
  statusIcon: {
    marginTop: theme.spacing(1),
  },
}));

const CustomerSearchField = ({ label, alwaysOn, ...props }) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;

      const filters = {
        ...nextFilterValues,
        or: [
          { accountName: { matchPhrasePrefix: `${value}` } },
          { accountName: { wildcard: `*${value}*` } },
          { lastName: { matchPhrasePrefix: `${value}` } },
          { lastName: { wildcard: `*${value}*` } },
          { email: { matchPhrasePrefix: `${value}` } },
          { email: { wildcard: `*${value}*` } },
        ],
      };

      setFilters(filters, displayedFilters);
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (mdDown) => [
  <AccountDropDown source="accountId.eq" label="Account" alwaysOn={!mdDown} />,
  <CustomerSearchField label="Search" alwaysOn />,
];

const ListActions = ({ basePath, xsDown, ...props }) => {
  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <CreateButton basePath={basePath} disabled={isSubscriptionDisabled()} />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const CustomerList = (props) => {
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [defaultFilter] = useState({
    reloadYardId: window.appUser?.reloadYardId,
    activeAccountNameLastName: { beginsWith: { active: "true" } },
  });

  const defaultSearchFilter = {
    reloadYardId: { eq: window.appUser?.reloadYardId },
    active: { eq: "true" },
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"customerByReloadYardByActiveByAccountNameByName", "search":"accountName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Customers",
    defaultFilter: defaultFilter,
    defaultSort: defaultSort,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    resource: "customerReloadYardAccounts",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Customers" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(mdDown)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        <CustomerGrid
          xsDown={xsDown}
          mdDown={mdDown}
          classes={classes}
          basePath="/customers"
          resource="customers"
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const CustomerGrid = (props) => {
  const { classes, xsDown, mdDown } = props;
  const { data, ids, page, ...context } = useListContext();

  // Transform the data here
  const transformedData = {};
  const transformedIds = [];

  if (data && ids) {
    ids.forEach((id) => {
      const record = data[id];
      if (record && record.customer) {
        // Transform the record here, for example, use the user subrecord
        transformedData[record.customerId] = {
          ...record.customer,
          accountName: record.accountName,
          account: record.account,
        };
        transformedIds.push(record.customerId);
      }
    });
  }

  const newContext = {
    ...context,
    data: transformedData,
    ids: transformedIds,
    basePath: props.basePath,
    resource: props.resource,
  };

  if (ids?.length === 0 && page === 1)
    return <Empty {...props} message="Invite a new customer" />;
  return (
    <ListContextProvider value={newContext}>
      {xsDown ? (
        <SimpleList
          primaryText={renderFullName}
          secondaryText={(record) => record.email}
          tertiaryText={accountNameRenderer}
          linkType="edit"
          leftIcon={(record) => <UserStatusIcon record={record} />}
          rightIcon={() => (
            <ChevronRightIcon
              fontSize="large"
              color="secondary"
              style={{ marginLeft: 40, marginTop: 10 }}
            />
          )}
        />
      ) : mdDown ? (
        <Datagrid rowClick="edit">
          <FunctionField
            source="accountName"
            label="Account"
            sortable={true}
            render={accountNameRenderer}
            sortBy={`{"key":"customerByReloadYardByActiveByAccountNameByName", "search":"accountName"}`}
          />
          <FunctionField
            source="lastName"
            label="Name"
            render={renderFullName}
            sortable={false}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          <TextField source="email" label="Email" sortable={false} />
        </Datagrid>
      ) : (
        <Datagrid rowClick="edit">
          <FunctionField
            source="accountName"
            label="Account"
            sortable={true}
            render={accountNameRenderer}
            sortBy={`{"key":"customerByReloadYardByActiveByAccountNameByName", "search":"accountName"}`}
          />
          <FunctionField
            source="lastName"
            label="Name"
            render={renderFullName}
            sortable={false}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          <TextField source="email" label="Email" sortable={false} />
          <TextField source="phone" label="Phone" sortable={false} />
          <EditButton />
        </Datagrid>
      )}
    </ListContextProvider>
  );
};
